import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import './DateSelect.css';
// 导入 SVG 图标组件
import { ReactComponent as ArrowFrontIcon } from '../../assets/svg/Schedule/arrow-front.svg';
import { ReactComponent as ArrowAfterIcon } from '../../assets/svg/Schedule/arrow-after.svg';
import { useSlotUpdates, useCalDateUpdates } from '../../hooks/dataUpdates';

interface DateSelectProps {
  /** 是否可见 */
  visible: boolean;
  /** 关闭回调 */
  onClose: () => void;
  /** 弹窗定位位置 */
  position: { top: number; left: number };
  /** 选中的年份 */
  selectedYear: number;
  /** 选中的月份 */
  selectedMonth: number;
  /** 选择月份的回调 */
  onSelectMonth: (year: number, month: number) => void;
  /** 是否启用日期选择（点击月份后可以选择具体日期） */
  enableDateSelection?: boolean;
  /** 选中的日期 */
  selectedDate?: Date | null;
  /** 选择日期的回调 */
  onSelectDate?: (date: Date) => void;
  /** 是否启用日期偏移确认（选择日期后询问是否偏移所有任务和计划日） */
  enableDateShiftConfirm?: boolean;
  /** 当前字段的原始日期（用于计算偏移天数） */
  originalDate?: Date | null;
  /** 拍摄计划ID（用于批量更新） */
  scheduleID?: string;
  /** 拍摄任务列表（用于批量更新） */
  slots?: Array<{ slotID: string; slotBelongScheduleID?: string; slotDate?: string | null; slotEndDay?: string | null }>;
  /** 计划日列表（用于批量更新） */
  calDates?: Array<{
    calDateID: string;
    calDateBelongScheduleID?: string;
    calDateDt?: string | null;
    calDateTittle?: string;
    calDateShift?: string;
    calDateSlotsSort?: string[];
  }>;
  /** 批量更新完成后的回调 */
  onBatchUpdateComplete?: () => void;
  /** 日期偏移回调（用于同步日历当前日期） */
  onDateShift?: (daysOffset: number) => void;
  /** 当前字段类型（'start' 表示开机日期，'end' 表示杀青日期） */
  dateFieldType?: 'start' | 'end';
}

const DateSelect: React.FC<DateSelectProps> = ({
  visible,
  onClose,
  position,
  selectedYear,
  selectedMonth,
  onSelectMonth,
  enableDateSelection = false,
  selectedDate,
  onSelectDate,
  enableDateShiftConfirm = false,
  originalDate,
  scheduleID,
  slots = [],
  calDates = [],
  onBatchUpdateComplete,
  onDateShift,
  dateFieldType,
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const { shiftSlotsDateData } = useSlotUpdates();
  const { shiftCalDatesDateData } = useCalDateUpdates();

  // 当前显示的年份和月份（用于切换）
  const [displayYear, setDisplayYear] = useState(selectedYear);
  const [_displayMonth, setDisplayMonth] = useState(selectedMonth);
  void _displayMonth;

  // 日期选择：是否显示日期列表（false 时显示月份宫格）
  const [showDateList, setShowDateList] = useState(false);
  const [dateListMonth, setDateListMonth] = useState(selectedMonth);
  const [dateListYear, setDateListYear] = useState(selectedYear);

  // 确认对话框状态
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingDate, setPendingDate] = useState<Date | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  const selectedDateKey = selectedDate
    ? `${selectedDate.getFullYear()}-${selectedDate.getMonth()}-${selectedDate.getDate()}`
    : '';
  const viewInitKeyRef = useRef('');

  // 打开弹窗或外部选中日期变化时初始化视图；避免父组件重渲染导致月份导航被重置
  useEffect(() => {
    if (!visible) {
      viewInitKeyRef.current = '';
      return;
    }

    const initKey = `${selectedYear}-${selectedMonth}-${selectedDateKey}-${enableDateSelection}`;
    if (viewInitKeyRef.current === initKey) return;
    viewInitKeyRef.current = initKey;

    setDisplayYear(selectedYear);
    setDisplayMonth(selectedMonth);
    setShowConfirmDialog(false);
    setPendingDate(null);
    setIsUpdating(false);

    // 如果启用日期选择且有选中的日期，直接显示日期列表
    if (enableDateSelection && selectedDate) {
      const dateYear = selectedDate.getFullYear();
      const dateMonth = selectedDate.getMonth() + 1;
      setDateListYear(dateYear);
      setDateListMonth(dateMonth);
      setShowDateList(true);
      // 同时更新显示的年月
      setDisplayYear(dateYear);
      setDisplayMonth(dateMonth);
    } else {
      setShowDateList(false);
      setDateListMonth(selectedMonth);
      setDateListYear(selectedYear);
    }
  }, [visible, selectedYear, selectedMonth, selectedDateKey, enableDateSelection, selectedDate]);

  // 点击外部关闭
  useEffect(() => {
    if (!visible) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (containerRef.current && !containerRef.current.contains(target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [visible, onClose]);

  // 切换到上一月/年
  const handlePreviousMonth = () => {
    if (showDateList) {
      if (dateListMonth === 1) {
        setDateListYear(dateListYear - 1);
        setDateListMonth(12);
      } else {
        setDateListMonth(dateListMonth - 1);
      }
    } else {
      setDisplayYear(displayYear - 1);
    }
  };

  // 切换到下一月/年
  const handleNextMonth = () => {
    if (showDateList) {
      if (dateListMonth === 12) {
        setDateListYear(dateListYear + 1);
        setDateListMonth(1);
      } else {
        setDateListMonth(dateListMonth + 1);
      }
    } else {
      setDisplayYear(displayYear + 1);
    }
  };

  // 选择月份
  const handleSelectMonth = (month: number) => {
    if (enableDateSelection) {
      setDateListYear(displayYear);
      setDateListMonth(month);
      setShowDateList(true);
    } else {
      onSelectMonth(displayYear, month);
    }
  };

  // 生成月份宫格（1-12月）
  const renderMonthGrid = () => {
    const months = Array.from({ length: 12 }, (_, i) => i + 1);
    return months.map((month) => {
      const isSelected = displayYear === selectedYear && month === selectedMonth;

      return (
        <div
          key={month}
          className={`date-select-month-cell ${isSelected ? 'selected' : ''}`}
          onClick={() => handleSelectMonth(month)}
        >
          {month}月
        </div>
      );
    });
  };

  // 计算日期差异（天数）
  const calculateDateDiff = (oldDate: Date, newDate: Date): number => {
    const diffTime = newDate.getTime() - oldDate.getTime();
    return Math.round(diffTime / (1000 * 60 * 60 * 24));
  };

  // 格式化日期偏移文本
  const formatDateShiftText = (days: number): string => {
    if (days === 0) return '0 天';
    if (days > 0) return `延后 ${days} 天`;
    return `提前 ${Math.abs(days)} 天`;
  };

  // 批量更新拍摄任务和计划日
  const handleBatchUpdate = async (newDate: Date) => {
    if (!scheduleID || !originalDate) return;

    setIsUpdating(true);
    const daysDiff = calculateDateDiff(originalDate, newDate);

    try {
      // 格式化原日期用于比较（修改开机日期时仅处理原开机日及之后的数据）
      const originalDateStr = originalDate
        ? `${originalDate.getFullYear()}-${String(originalDate.getMonth() + 1).padStart(2, '0')}-${String(originalDate.getDate()).padStart(2, '0')}`
        : null;

      // 1. 批量偏移拍摄任务日期（使用统一的更新 hook，自动更新缓存和通知订阅者）
      const relatedSlots = slots.filter(pt => {
        if (pt.slotBelongScheduleID !== scheduleID || !pt.slotDate) return false;
        if (dateFieldType === 'start' && originalDateStr) {
          const slotDateStr = pt.slotDate.split('T')[0];
          if (slotDateStr < originalDateStr) return false;
        }
        return true;
      });

      if (relatedSlots.length > 0) {
        const slotIDs = relatedSlots.map(pt => pt.slotID);
        await shiftSlotsDateData(scheduleID, slotIDs, daysDiff);
      }

      // 2. 批量偏移计划日日期（使用统一的更新 hook，自动更新缓存和通知订阅者）
      const relatedCalDates = calDates.filter(pd => {
        if (pd.calDateBelongScheduleID !== scheduleID || !pd.calDateDt) return false;
        if (dateFieldType === 'start' && originalDateStr) {
          const dateStr = pd.calDateDt.split('T')[0];
          if (dateStr < originalDateStr) return false;
        }
        return true;
      });

      if (relatedCalDates.length > 0) {
        const calDateIDs = relatedCalDates.map(pd => pd.calDateID);
        // 如果是修改开机日期，传递原开机日期用于判断只处理原开机日及之后的 CalDate
        const originalStartDate = dateFieldType === 'start' ? originalDate : undefined;
        console.log('批量偏移计划日日期:', {
          calDateIDs,
          daysDiff,
          originalStartDate,
          requestData: { calDateIDs, daysOffset: daysDiff, originalStartDate }
        });
        await shiftCalDatesDateData(scheduleID, calDateIDs, daysDiff, originalStartDate);
      }

      // 3. 更新该字段的日期（scheduleStartDate 或 scheduleEndDate）
      // 在批量更新完成后更新，确保数据一致性
      if (onSelectDate) {
        await onSelectDate(newDate);
      }

      // 4. 如果是开机日期，同步顺延日历当前日期
      if (dateFieldType === 'start' && onDateShift) {
        onDateShift(daysDiff);
      }

      // 5. 通知父组件更新完成（刷新数据）
      // 等待刷新完成后再关闭弹窗，确保数据已更新
      if (onBatchUpdateComplete) {
        await onBatchUpdateComplete();
      }

      // 5. 关闭弹窗
      setShowConfirmDialog(false);
      setPendingDate(null);
      setIsUpdating(false);
      onClose();
    } catch (error: any) {
      console.error('批量更新失败:', error);
      console.error('错误响应数据:', error.response?.data);
      console.error('错误状态码:', error.response?.status);
      console.error('请求URL:', error.config?.url);
      console.error('请求数据:', error.config?.data);
      setIsUpdating(false);
      // 显示用户友好的错误提示
      const errorMessage = error.response?.data?.message || error.message || '批量更新失败，请稍后重试';
      const displayMessage = Array.isArray(errorMessage) ? errorMessage.join('; ') : errorMessage;
      alert(t('common.batchUpdateFailedWithAdvice', { message: displayMessage }));
    }
  };

  // 处理确认对话框的确定按钮
  const handleConfirm = () => {
    if (pendingDate) {
      handleBatchUpdate(pendingDate);
    }
  };

  // 处理确认对话框的取消按钮
  const handleCancel = () => {
    setShowConfirmDialog(false);
    setPendingDate(null);
  };

  // 生成日期列表（用于日期选择）
  const renderDateList = () => {
    const daysInMonth = new Date(dateListYear, dateListMonth, 0).getDate();
    const firstDay = new Date(dateListYear, dateListMonth - 1, 1);
    const firstDayOfWeek = firstDay.getDay(); // 0 = 周日

    return (
      <div className="date-select-date-list">
        {/* 表头 */}
        <div className="date-select-week-header">
          {['日', '一', '二', '三', '四', '五', '六'].map((day, index) => (
            <div key={index} className="date-select-week-header-cell">
              {day}
            </div>
          ))}
        </div>

        {/* 日期网格 */}
        <div className="date-select-date-grid">
          {/* 填充上个月的日期（占位） */}
          {Array.from({ length: firstDayOfWeek }, (_, i) => (
            <div key={`empty-${i}`} className="date-select-date-cell empty"></div>
          ))}

          {/* 本月的日期 */}
          {Array.from({ length: daysInMonth }, (_, i) => {
            const day = i + 1;
            const date = new Date(dateListYear, dateListMonth - 1, day);
            const isSelected = selectedDate &&
              date.getFullYear() === selectedDate.getFullYear() &&
              date.getMonth() === selectedDate.getMonth() &&
              date.getDate() === selectedDate.getDate();

            return (
              <div
                key={day}
                className={`date-select-date-cell ${isSelected ? 'selected' : ''}`}
                onClick={() => {
                  if (onSelectDate) {
                    // 如果启用日期偏移确认且有原始日期，显示确认对话框
                    if (enableDateShiftConfirm && originalDate && scheduleID) {
                      setPendingDate(date);
                      setShowConfirmDialog(true);
                    } else {
                      // 否则直接调用回调
                      onSelectDate(date);
                      onClose();
                    }
                  }
                }}
              >
                {day}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (!visible) return null;

  return createPortal(
    <div
      className="date-select-overlay"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 10000,
        pointerEvents: 'none',
      }}
    >
      <div
        ref={containerRef}
        className="date-select-container"
        style={{
          position: 'fixed',
          top: `${position.top}px`,
          left: `${position.left}px`,
          pointerEvents: 'auto',
        }}
      >
        <div className="date-select-dialog">
          {/* Header */}
          <div className="date-select-header">
            <button
              className="date-select-nav-btn"
              onClick={handlePreviousMonth}
            >
              <ArrowFrontIcon className="date-select-nav-icon" aria-label={t('schedule.prevMonth')} />
            </button>

            <div className="date-select-year-month">
              <span className="date-select-year">{showDateList ? dateListYear : displayYear}</span>
              {showDateList && (
                <>
                  <span className="date-select-year-month-separator">年</span>
                  <span className="date-select-month">{dateListMonth}月</span>
                </>
              )}
            </div>

            <button
              className="date-select-nav-btn"
              onClick={handleNextMonth}
            >
              <ArrowAfterIcon className="date-select-nav-icon" aria-label={t('schedule.nextMonth')} />
            </button>
          </div>

          {/* Content */}
          <div className="date-select-content">
            {showConfirmDialog ? (
              // 确认对话框
              <div className="date-select-confirm-dialog">
                {isUpdating ? (
                  <div className="date-select-updating">
                    <div className="date-select-updating-text">{t('schedule.updating')}</div>
                  </div>
                ) : (
                  <>
                    <div className="date-select-confirm-message">
                      {pendingDate && originalDate && (
                        <span>
                          {t('schedule.dateShiftNotice')}{formatDateShiftText(calculateDateDiff(originalDate, pendingDate))}
                        </span>
                      )}
                    </div>
                    <div className="date-select-confirm-buttons">
                      <button
                        className="date-select-confirm-btn date-select-confirm-btn-primary"
                        onClick={handleConfirm}
                      >
                        {t('common.confirm')}
                      </button>
                      <button
                        className="date-select-confirm-btn date-select-confirm-btn-secondary"
                        onClick={handleCancel}
                      >
                        {t('common.cancel')}
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : showDateList ? (
              renderDateList()
            ) : (
              <div className="date-select-month-grid">
                {renderMonthGrid()}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default DateSelect;
